import { getSettings } from '@/data/Settings';
import { ID } from '@/data/types/Basic';
import { Cache } from '@/data/types/Cache';
import { getRequestId } from '@/data/utils/getRequestId';
import { getServerSideCommon } from '@/data/utils/getServerSideCommon';
import { traceWithId } from '@/data/utils/loggerUtil';
import {
	findStoreLocationByPhysicalStoreId,
	resolveStoreLocation,
} from '@/data/utils/storeLocationResolver';
import { findStoreDataByStLocId } from '@/utils/storeLocatorUtils';
import Cookies from 'cookies';
import customSetDefaultStore from 'integration/generated/custom/customSetDefaultStore';
import {
	ComHdmCommerceRestStoreHandlerHDMStoreHandlerSetDefaultRequest,
	ComHdmCommerceRestStoreHandlerHDMStoreHandlerSetDefaultResponse,
} from 'integration/generated/custom/data-contracts';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { GetServerSidePropsContext } from 'next';
import { useMemo } from 'react';
import { useCookies } from 'react-cookie';
import {
	DEFAULT_STATE_ID,
	DEFAULT_STORE_ID,
	DEFAULT_UNIQUE_ID,
	GEO_LOCATION_COOKIE_NAME,
	MIN_EXP_TIME,
	WC_DEFAULT_STORE_COOKIE,
	WC_MARKET_ID_COOKIE,
	WC_ST_FIND_COOKIE,
	WC_ST_PROV_COOKIE,
} from 'shared/custom/constants/commonConstants';
import { parseExpTime, setCookie } from 'shared/custom/utils/cookieHandler';
import { getStoreDataByPhysicalStoreId } from '../utils/commonIntegrationUtils';

export const setDefaultFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		query: {
			[key: string]: string | boolean | ID[] | number;
		},
		data: ComHdmCommerceRestStoreHandlerHDMStoreHandlerSetDefaultRequest,
		params: RequestParams
	) =>
		(await customSetDefaultStore(pub).setDefault(
			storeId,
			query,
			data,
			params
		)) as ComHdmCommerceRestStoreHandlerHDMStoreHandlerSetDefaultResponse;

export type DefaultStore = {
	defaultStore: string;
	fromPageName: string;
};

export type PhysicalStoreType = {
	uniqueId: string;
	physicalStoreId: string;
};

export const getDefaultStore = async (
	cache: Cache,
	context: GetServerSidePropsContext
): Promise<PhysicalStoreType> => {
	traceWithId(getRequestId(context), 'getDefaultStore: start');
	const settings = await getSettings(cache, context);
	const { storeId, langId } = getServerSideCommon(settings, context);
	const cookies = new Cookies(context.req, context.res);
	const storeData = resolveStoreLocation(context);

	const fetchResponse = await setDefaultFetcher(false)(
		storeId,
		{ responseFormat: 'json', langId },
		{
			defaultStore: storeData.stLocId,
			fromPageName: '2',
		},
		{}
	);
	const response: PhysicalStoreType = {
		uniqueId: fetchResponse.uniqueId,
		physicalStoreId: storeData.stLocId,
	};
	if (storeData.stLocId) {
		const stateFullData = findStoreDataByStLocId(storeData.stLocId);
		if (stateFullData) {
			const store = stateFullData.stores?.find((s) => s.stLocId === storeData.stLocId);
			const marketId = store?.marketId || '21';
			cookies.set(WC_MARKET_ID_COOKIE, marketId, {
				expires: parseExpTime(MIN_EXP_TIME),
				httpOnly: false,
				secure: false,
			});
		}
	}
	cookies.set(WC_ST_FIND_COOKIE, '1', {
		httpOnly: false,
		secure: false,
	});
	cookies.set(WC_ST_PROV_COOKIE, storeData.stateId, {
		httpOnly: false,
		secure: false,
	});
	cookies.set(WC_DEFAULT_STORE_COOKIE, storeData.stLocId, {
		expires: parseExpTime(MIN_EXP_TIME),
		httpOnly: false,
		secure: false,
	});
	cookies.set(
		GEO_LOCATION_COOKIE_NAME,
		JSON.stringify({ ...storeData, uniqueId: response.uniqueId }),
		{
			expires: parseExpTime(MIN_EXP_TIME),
			httpOnly: false,
			secure: false,
		}
	);
	traceWithId(getRequestId(context), 'getDefaultStore: end (filled cache)');
	return response;
};

export const setDefaultRequest = async (
	{ defaultStore, fromPageName }: DefaultStore,
	storeId: string,
	langId: string,
	cookieExpiryTime = MIN_EXP_TIME,
	params: RequestParams = {}
) => {
	if (!defaultStore) return;
	const resp = await setDefaultFetcher(true)(
		storeId,
		{ responseFormat: 'json', langId },
		{ defaultStore, fromPageName },
		params
	);

	const storeLocation = findStoreLocationByPhysicalStoreId(defaultStore);
	const storeFullData = getStoreDataByPhysicalStoreId(defaultStore);

	setCookie(
		GEO_LOCATION_COOKIE_NAME,
		JSON.stringify({
			hasPreviousAnswer: true,
			stLocId: defaultStore,
			stateId: storeLocation?.stateId ?? DEFAULT_STATE_ID,
			uniqueId: resp.uniqueId ?? '',
		}),
		cookieExpiryTime
	);
	setCookie(WC_MARKET_ID_COOKIE, storeFullData?.marketId || '21', cookieExpiryTime);
	setCookie(WC_DEFAULT_STORE_COOKIE, defaultStore, cookieExpiryTime);

	return resp;
};
/**
 * Resolves the current physicalStoreId and uniqueId based on the geo location cookie, to use in the
 * client side, it will return the default store if there is no cookie or the cookie is invalid
 *
 */
export const useDefaultStore = () => {
	const [cookies] = useCookies([GEO_LOCATION_COOKIE_NAME]);
	const geoCookie = cookies?.[GEO_LOCATION_COOKIE_NAME];
	const physicalStoreId = geoCookie?.stLocId ? geoCookie.stLocId : DEFAULT_STORE_ID;
	const stateId = geoCookie?.stateId ? geoCookie.stateId : DEFAULT_STATE_ID;
	const uniqueId = geoCookie?.uniqueId ? geoCookie.uniqueId : DEFAULT_UNIQUE_ID;

	const storeData = useMemo(
		() => getStoreDataByPhysicalStoreId(physicalStoreId),
		[physicalStoreId]
	);

	return {
		uniqueId,
		physicalStoreId,
		stateId,
		storeData,
	};
};
